<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { MegaphoneIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import {
  useBannerStore,
  type BannerAdminItem,
  type BannerVariant,
  type BannerPayload,
} from '@/stores/useBannerStore'

const store = useBannerStore()

// BU connues (alignées sur l'enum Postgres BU).
const BU_OPTIONS = [
  'UNIXO_ONE',
  'UNIXO_CORP',
  'UNIXO_FACTORY',
  'UNIXO_SECURE',
  'UNIXO_NEGOCE',
  'UNIXO_ACR',
  'ODY_C',
  'OOB',
  'GROUP_SOLUTIONS',
]

const VARIANTS: { value: BannerVariant; label: string }[] = [
  { value: 'info', label: 'Info (bleu)' },
  { value: 'success', label: 'Succès (vert)' },
  { value: 'warning', label: 'Alerte (orange)' },
  { value: 'error', label: 'Erreur (rouge)' },
  { value: 'neutral', label: 'Neutre' },
  { value: 'custom', label: 'Personnalisé' },
]

const VARIANT_BADGE: Record<BannerVariant, string> = {
  info: 'badge-info',
  success: 'badge-success',
  warning: 'badge-warning',
  error: 'badge-error',
  neutral: 'badge-neutral',
  custom: 'badge-ghost',
}

onMounted(() => store.fetchAll())

// ── Éditeur ───────────────────────────────────────────────────────────────────
const showEditor = ref(false)
const editingId = ref<string | null>(null)
const form = ref({
  text: '',
  link: '',
  variant: 'info' as BannerVariant,
  bgColor: '#1d4ed8',
  textColor: '#ffffff',
  active: true,
  startsAt: '',
  endsAt: '',
  permissionsText: '',
  bus: [] as string[],
  priority: 0,
})

function isoToLocal(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 16)
}
function localToIso(local: string): string | null {
  return local ? new Date(local).toISOString() : null
}

function openCreate() {
  editingId.value = null
  form.value = {
    text: '',
    link: '',
    variant: 'info',
    bgColor: '#1d4ed8',
    textColor: '#ffffff',
    active: true,
    startsAt: '',
    endsAt: '',
    permissionsText: '',
    bus: [],
    priority: 0,
  }
  showEditor.value = true
}

function openEdit(b: BannerAdminItem) {
  editingId.value = b.id
  form.value = {
    text: b.text,
    link: b.link ?? '',
    variant: b.variant,
    bgColor: b.bgColor ?? '#1d4ed8',
    textColor: b.textColor ?? '#ffffff',
    active: b.active,
    startsAt: isoToLocal(b.startsAt),
    endsAt: isoToLocal(b.endsAt),
    permissionsText: b.permissions.join(', '),
    bus: [...b.bus],
    priority: b.priority,
  }
  showEditor.value = true
}

const canSave = computed(() => form.value.text.trim().length > 0)

async function save() {
  const f = form.value
  const payload: BannerPayload = {
    text: f.text.trim(),
    link: f.link.trim() || null,
    variant: f.variant,
    bgColor: f.variant === 'custom' ? f.bgColor : null,
    textColor: f.variant === 'custom' ? f.textColor : null,
    active: f.active,
    startsAt: localToIso(f.startsAt),
    endsAt: localToIso(f.endsAt),
    permissions: f.permissionsText.split(',').map((s) => s.trim()).filter(Boolean),
    bus: f.bus,
    priority: Number(f.priority) || 0,
  }
  const ok = editingId.value ? await store.update(editingId.value, payload) : await store.create(payload)
  if (ok) showEditor.value = false
}

async function confirmDelete(b: BannerAdminItem) {
  if (!window.confirm(`Supprimer le bandeau « ${b.text.slice(0, 40)}… » ?`)) return
  await store.remove(b.id)
}

function fmtDate(iso: string | null) {
  return iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'
}
function periode(b: BannerAdminItem) {
  if (!b.startsAt && !b.endsAt) return 'Toujours'
  return `${fmtDate(b.startsAt)} → ${fmtDate(b.endsAt)}`
}
function cible(b: BannerAdminItem) {
  const parts: string[] = []
  if (b.permissions.length) parts.push(b.permissions.join(', '))
  if (b.bus.length) parts.push(b.bus.join(', '))
  return parts.length ? parts.join(' · ') : 'Tout le monde'
}
</script>

<template>
  <div class="p-6 space-y-6">
    <div class="flex items-center justify-between flex-wrap gap-3">
      <Bs2Title title="Bandeaux d'information" :icon="MegaphoneIcon" subtitle="Messages défilants affichés en haut de l'application" />
      <button class="btn btn-sm btn-primary" @click="openCreate">
        <PlusIcon class="size-4" /> Nouveau bandeau
      </button>
    </div>

    <div v-if="store.isLoading" class="flex justify-center py-12">
      <span class="loading loading-spinner loading-lg" />
    </div>

    <div v-else-if="store.items.length" class="overflow-x-auto rounded-xl border border-base-300">
      <table class="table table-sm w-full">
        <thead>
          <tr>
            <th>Texte</th>
            <th>Style</th>
            <th>État</th>
            <th>Période</th>
            <th>Cible</th>
            <th class="text-right">Priorité</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="b in store.items" :key="b.id" class="hover">
            <td class="max-w-md truncate">{{ b.text }}</td>
            <td><span :class="['badge badge-sm', VARIANT_BADGE[b.variant]]">{{ b.variant }}</span></td>
            <td>
              <span :class="['badge badge-sm', b.active ? 'badge-success' : 'badge-ghost']">
                {{ b.active ? 'Actif' : 'Inactif' }}
              </span>
            </td>
            <td class="text-xs">{{ periode(b) }}</td>
            <td class="text-xs">{{ cible(b) }}</td>
            <td class="text-right">{{ b.priority }}</td>
            <td class="text-right whitespace-nowrap">
              <button class="btn btn-ghost btn-xs" @click="openEdit(b)"><PencilSquareIcon class="size-4" /></button>
              <button class="btn btn-ghost btn-xs text-error" @click="confirmDelete(b)"><TrashIcon class="size-4" /></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="flex flex-col items-center gap-3 py-16 text-base-content/40">
      <MegaphoneIcon class="size-12" />
      <p>Aucun bandeau pour l'instant</p>
    </div>

    <!-- Éditeur -->
    <dialog class="modal" :class="{ 'modal-open': showEditor }">
      <div class="modal-box max-w-2xl">
        <h3 class="font-semibold text-lg mb-4">{{ editingId ? 'Modifier le bandeau' : 'Nouveau bandeau' }}</h3>

        <div class="space-y-3">
          <label class="form-control">
            <span class="label-text text-xs">Texte</span>
            <textarea v-model="form.text" rows="2" class="textarea textarea-bordered" placeholder="Message à afficher…" />
          </label>

          <label class="form-control">
            <span class="label-text text-xs">Lien (optionnel) — rend le bandeau cliquable</span>
            <input v-model="form.link" type="text" class="input input-sm input-bordered" placeholder="/dashboard/orders ou https://…" />
          </label>

          <div class="flex gap-3 flex-wrap items-end">
            <label class="form-control">
              <span class="label-text text-xs">Style</span>
              <select v-model="form.variant" class="select select-sm select-bordered">
                <option v-for="v in VARIANTS" :key="v.value" :value="v.value">{{ v.label }}</option>
              </select>
            </label>
            <template v-if="form.variant === 'custom'">
              <label class="form-control">
                <span class="label-text text-xs">Fond (hex, transparence ok)</span>
                <div class="flex items-center gap-2">
                  <span
                    class="size-8 shrink-0 rounded border border-base-300"
                    :style="{ backgroundColor: form.bgColor || 'transparent' }"
                  />
                  <input
                    v-model="form.bgColor"
                    type="text"
                    class="input input-sm input-bordered w-36 font-mono"
                    placeholder="#fd5861 ou #fd586126"
                  />
                </div>
              </label>
              <label class="form-control">
                <span class="label-text text-xs">Texte (hex)</span>
                <div class="flex items-center gap-2">
                  <span
                    class="size-8 shrink-0 rounded border border-base-300"
                    :style="{ backgroundColor: form.textColor || 'transparent' }"
                  />
                  <input
                    v-model="form.textColor"
                    type="text"
                    class="input input-sm input-bordered w-36 font-mono"
                    placeholder="#ffffff"
                  />
                </div>
              </label>
            </template>
            <label class="form-control">
              <span class="label-text text-xs">Priorité</span>
              <input v-model.number="form.priority" type="number" step="1" class="input input-sm input-bordered w-24" />
            </label>
            <label class="label cursor-pointer gap-2">
              <input v-model="form.active" type="checkbox" class="toggle toggle-sm toggle-success" />
              <span class="label-text text-xs">Actif</span>
            </label>
          </div>

          <div class="flex gap-3 flex-wrap">
            <label class="form-control">
              <span class="label-text text-xs">Début (optionnel)</span>
              <input v-model="form.startsAt" type="datetime-local" class="input input-sm input-bordered" />
            </label>
            <label class="form-control">
              <span class="label-text text-xs">Fin (optionnel)</span>
              <input v-model="form.endsAt" type="datetime-local" class="input input-sm input-bordered" />
            </label>
          </div>

          <label class="form-control">
            <span class="label-text text-xs">Permissions ciblées (codes séparés par des virgules) — vide = tout le monde</span>
            <input v-model="form.permissionsText" type="text" class="input input-sm input-bordered" placeholder="ex. SALES_READ, ADV_ADMIN" />
          </label>

          <div class="form-control">
            <span class="label-text text-xs mb-1">BU ciblées — vide = toutes</span>
            <div class="flex flex-wrap gap-2">
              <label v-for="bu in BU_OPTIONS" :key="bu" class="label cursor-pointer gap-1 border border-base-300 rounded-lg px-2 py-1">
                <input v-model="form.bus" type="checkbox" :value="bu" class="checkbox checkbox-xs" />
                <span class="label-text text-xs">{{ bu }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="modal-action">
          <button class="btn btn-ghost" @click="showEditor = false">Annuler</button>
          <button class="btn btn-primary" :disabled="!canSave || store.isSaving" @click="save">
            <span v-if="store.isSaving" class="loading loading-spinner loading-xs" />
            Enregistrer
          </button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop" @click="showEditor = false"><button>close</button></form>
    </dialog>
  </div>
</template>
